import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Retain the homepage position while its component is unmounted.
let savedPosition = null;

export default function useHomeScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const restoredEntryKey = useRef(
    savedPosition && navigationType === 'POP' ? location.key : null,
  );

  useLayoutEffect(() => {
    const rememberPosition = () => {
      savedPosition = { top: window.scrollY, left: window.scrollX };
    };

    // A history return restores the actual position, even if the URL has
    // an older section hash. Explicit section links still select that section.
    if (!location.hash || restoredEntryKey.current === location.key) {
      window.scrollTo({
        ...(savedPosition || { top: 0, left: 0 }),
        behavior: 'instant',
      });
    }

    window.addEventListener('scroll', rememberPosition, { passive: true });
    return () => window.removeEventListener('scroll', rememberPosition);
    // Restore only on mount; scrolling within the homepage is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!location.hash || restoredEntryKey.current === location.key) {
      return undefined;
    }

    let sectionId;
    try {
      sectionId = decodeURIComponent(location.hash.slice(1));
    } catch {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'instant'
          : 'smooth',
        block: 'start',
      });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.hash, location.key]);
}
